// @flow

import React, { Fragment } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { reduxForm, getFormValues, change } from 'redux-form';
import type { FormProps } from 'redux-form/lib/types.js.flow';

import get from 'lodash/get';

import moment from 'moment';

import Fieldset from '@clark-inc/clark-forms';
import { Grid, Row, Col, BreadcrumbsHeader, SPACING } from 'clark-styles';

import { postSessionCreate } from 'actions/sessions';

import Button from 'components/shared/button';

import { type StateType as ReducerStateType } from 'reducers';
import { type ClientsType } from 'types/clients';
import { type EngagementsType } from 'types/engagements';
import { type SubjectsType } from 'types/subjects';

import {
  generateEngagementDropdownOptions,
  transformAndPost,
  calculateCTACopy,
  calculateRecurrenceCopy,
  isScheduling,
  getTimeObjFromDateTime,
} from './helpers';
import {
  generateFieldset,
  generateRecurrenceFieldset,
  generateSecondaryFieldset,
} from './fieldset';
import { HelperCopy } from './styles';

// RENDER

const SESSION_CREATE = 'sessionCreate';
const breadcrumbs = [{ path: '/sessions', displayName: 'Cancel' }];

type BasePropsType = {
  engagements: EngagementsType,
  startTime: moment,
  subjects: SubjectsType,
  clients: ClientsType,
  isCancelation: boolean,
};

const toggleIsCancelation = () => change(SESSION_CREATE, 'isCancelation', false, false, false);
const toggleIsRecurring = () => change(SESSION_CREATE, 'isRecurring', false, false, false);

type DispatchPropsType = {
  dispatchPostSessionCreate: typeof postSessionCreate,
  dispatchToggleIsCancelation: typeof toggleIsCancelation,
  dispatchToggleIsRecurring: typeof toggleIsRecurring,
};

const mapDispatchToProps: DispatchPropsType = {
  dispatchPostSessionCreate: postSessionCreate,
  dispatchToggleIsCancelation: toggleIsCancelation,
  dispatchToggleIsRecurring: toggleIsRecurring,
};

type PropsFromStateType = {
  isCancelation: boolean,
  isRecurring: boolean,
  sessionDate: ?moment,
  sessionRecurrenceCount: ?number,
  endTime: { time: string, period: string },
};

const mapStateToProps = (state: ReducerStateType): PropsFromStateType => {
  const { isCancelation, isRecurring, startDate, endTime, sessionRecurrenceCount } =
    getFormValues(SESSION_CREATE)(state) || {};
  const { router } = state;

  const start = get(router, 'location.state.start');
  const initialStartDate =
    start &&
    moment(start)
      .startOf('day')
      .toISOString();
  const initialStartTime = getTimeObjFromDateTime(start);
  const end = get(router, 'location.state.end');
  const initialEndTime = getTimeObjFromDateTime(end);

  const initialSessionRecurrenceCount = 16;

  return {
    isCancelation,
    isRecurring,
    sessionDate: startDate,
    sessionRecurrenceCount,
    endTime,
    initialValues: {
      startDate: initialStartDate,
      startTime: initialStartTime,
      endTime: initialEndTime,
      sessionRecurrenceCount: initialSessionRecurrenceCount,
    },
  };
};

type PropsType = PropsFromStateType & DispatchPropsType & BasePropsType & FormProps;

const SessionsCreateForm = ({
  dispatchPostSessionCreate,
  dispatchToggleIsCancelation,
  dispatchToggleIsRecurring,
  handleSubmit,
  isCancelation,
  isRecurring,
  sessionDate,
  sessionRecurrenceCount,
  startTime,
  endTime,
  submitting,
  pristine,
  subjects,
  engagements,
  clients,
}: PropsType) => {
  const engagementsDropdown = generateEngagementDropdownOptions(engagements, clients, subjects);
  return (
    <Fragment>
      <BreadcrumbsHeader breadcrumbs={breadcrumbs} title="Create Session" />
      <Grid marginTop={SPACING.S_4}>
        <Row>
          <Col sm={12} mdOffset={3} md={6} lgOffset={3} lg={6}>
            <form onSubmit={handleSubmit(transformAndPost(dispatchPostSessionCreate))}>
              <Fieldset
                data={generateFieldset(
                  engagementsDropdown,
                  dispatchToggleIsRecurring,
                  isRecurring,
                  startTime,
                )}
              />
              {isScheduling(sessionDate, endTime) && (
                <Fragment>
                  <Fieldset
                    data={generateRecurrenceFieldset(
                      sessionDate,
                      isRecurring,
                      dispatchToggleIsCancelation,
                    )}
                  />
                  {isRecurring && (
                    <HelperCopy>
                      {calculateRecurrenceCopy(sessionDate, sessionRecurrenceCount)}
                    </HelperCopy>
                  )}
                </Fragment>
              )}
              <Fieldset data={generateSecondaryFieldset(isRecurring, sessionDate, endTime)} />
              <Button disabled={pristine || submitting} layout="fullWidth">
                {calculateCTACopy(isCancelation, isRecurring, sessionDate, endTime)}
              </Button>
            </form>
          </Col>
        </Row>
      </Grid>
    </Fragment>
  );
};

// EXPORT

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  reduxForm({
    form: SESSION_CREATE,
  }),
)(SessionsCreateForm);



// WEBPACK FOOTER //
// ./src/containers/sessions/create/form/index.js